import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Rocket, Sparkles, Star, Users } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import SEO, { SITE_URL, SITE_NAME } from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import ReviewsSection from "@/components/ReviewsSection";
import ReportDialog from "@/components/ReportDialog";
import RelatedServers from "@/components/RelatedServers";
import { useAuth } from "@/context/AuthContext";
import { trackJoinClick, trackServerView } from "@/lib/analytics";

// Same deterministic gradient logic as ServerCard so a server without a
// custom banner shows the same generated background here.
function detailBannerGradient(id) {
  if (!id) return "linear-gradient(135deg, #1a1a2e, #16213e)";
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 40 + (Math.abs(hash >> 8) % 60)) % 360;
  const h3 = (h1 + 200) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 65%, 22%) 0%, hsl(${h2}, 60%, 32%) 55%, hsl(${h3}, 70%, 18%) 100%)`;
}
const ICON_FALLBACK =
  "https://images.unsplash.com/photo-1614680376739-414d95ff43df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxkaXNjb3JkJTIwc2VydmVyJTIwZ2FtaW5nfGVufDB8fHx8MTc4Mjk5NDE5Nnww&ixlib=rb-4.1.0&q=85";

export default function ServerDetail() {
  const { slug } = useParams();
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  // Distinguish "server is confirmed dead/missing" (404/410 from API) from
  // "we hit a transient error" (5xx, timeout, network blip). Only the
  // confirmed-dead case gets a noindex meta — otherwise Googlebot's
  // second-pass JS render was de-indexing valid servers whenever the API
  // had a brief hiccup during crawl. That bug cost us ~65 pages in GSC.
  const [confirmedMissing, setConfirmedMissing] = useState(false);
  const [bannerBroken, setBannerBroken] = useState(false);
  // Bump cooldown state (unix seconds; max of per-server 2h and global 30m).
  const [bumpReadyAt, setBumpReadyAt] = useState(0);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const { user, loginWithDiscord } = useAuth();
  const login = loginWithDiscord;
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/servers/${slug}`);
        setServer(data);
        setConfirmedMissing(false);
        setBannerBroken(false);
        // Fire one view ping per detail page load. Idempotent-ish thanks to
        // the backend's per-(ip, server) 60-second rate-limit.
        if (data?.id) trackServerView(data.id);
        // If URL used raw id but server has slug, replace URL to canonical slug (no reload)
        if (data.slug && data.slug !== slug) {
          window.history.replaceState({}, "", `/servers/${data.slug}`);
        }
      } catch (err) {
        // Only mark as confirmed-missing if the API explicitly says so.
        // Transient errors (5xx, timeout, offline) must NOT emit noindex,
        // or Googlebot will de-index perfectly valid URLs on a bad render.
        const code = err?.response?.status;
        const missing = code === 404 || code === 410;
        setConfirmedMissing(missing);
        setServer(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  // Pull the user's live bump cooldown for this server so the button starts
  // in the correct state on page-load (survives refreshes and cross-device).
  useEffect(() => {
    if (!user || !server?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/servers/${server.id}/bump-status`);
        if (cancelled) return;
        const next = Math.max(
          data.next_bump_at || 0,
          data.global_next_bump_at || 0,
        );
        if (next > Math.floor(Date.now() / 1000)) setBumpReadyAt(next);
      } catch {
        // 401 (not logged in) or 404 (server gone) — just skip.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, server?.id]);

  // 1-second heartbeat only while a cooldown is active.
  useEffect(() => {
    if (bumpReadyAt <= 0) return undefined;
    setNowSec(Math.floor(Date.now() / 1000));
    const id = setInterval(() => {
      const s = Math.floor(Date.now() / 1000);
      setNowSec(s);
      if (s >= bumpReadyAt) setBumpReadyAt(0);
    }, 1000);
    return () => clearInterval(id);
  }, [bumpReadyAt]);

  const refreshServer = async () => {
    try {
      const { data } = await api.get(`/servers/${slug}`);
      setServer(data);
    } catch {
      // ignore
    }
  };

  const secondsLeft = Math.max(0, bumpReadyAt - nowSec);
  const onCooldown = secondsLeft > 0;
  const cooldownLabel = (() => {
    if (secondsLeft <= 0) return "";
    const h = Math.floor(secondsLeft / 3600);
    const m = Math.floor((secondsLeft % 3600) / 60);
    const s = secondsLeft % 60;
    const pad = (n) => n.toString().padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  })();

  const handleBump = async () => {
    if (!user) return login();
    if (onCooldown) return; // button is disabled visually
    try {
      const { data } = await api.post(`/servers/${server?.id}/bump`);
      setServer((s) => ({ ...s, bumped_at: data.bumped_at }));
      const next = Math.max(
        data.next_bump_at || 0,
        data.global_next_bump_at || 0,
      );
      if (next) setBumpReadyAt(next);
      toast.success("Bumped! This server just jumped to the top.");
    } catch (err) {
      const body = err?.response?.data;
      // Start the countdown even if the request failed with a cooldown.
      if (body?.next_bump_at) setBumpReadyAt(body.next_bump_at);
      toast.error(body?.detail || "Bump failed");
    }
  };

  if (loading) {
    return (
      <div className="pt-32 max-w-5xl mx-auto px-4">
        <div className="h-64 rounded-2xl bg-white/5 animate-pulse" />
      </div>
    );
  }
  if (!server) {
    // Fixes Google's "Soft 404" flag: return a real not-found UI. Emit
    // `noindex` ONLY when the API explicitly confirmed the server is
    // missing (404/410). Transient errors render the same UI without
    // noindex so a flaky Googlebot render never de-indexes valid pages.
    return (
      <div className="pt-32 pb-16 max-w-xl mx-auto px-4 sm:px-6 text-center" data-testid="server-not-found">
        <SEO
          title={confirmedMissing ? "Server not found" : "Server temporarily unavailable"}
          description={confirmedMissing
            ? "This Discord server listing is no longer available on Serverlistr."
            : "This Discord server listing is temporarily unavailable. Please try again."}
          path={`/servers/${slug}`}
          noindex={confirmedMissing}
        />
        <h1 className="font-heading text-3xl sm:text-4xl font-black">
          {confirmedMissing ? "Server not found" : "Temporarily unavailable"}
        </h1>
        <p className="mt-3 text-sm text-zinc-400">
          {confirmedMissing
            ? "This listing was removed, or the invite is no longer valid."
            : "We couldn't load this listing right now. Please refresh in a moment."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/servers"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold"
          >
            Browse all servers
          </Link>
          <Link
            to="/best/discord-servers"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 font-semibold text-white"
          >
            Top Discord servers
          </Link>
        </div>
      </div>
    );
  }

  const bannerUrl = !bannerBroken ? server.banner_url : null;
  const icon = server.icon_url || ICON_FALLBACK;

  return (
    <div className="pt-20 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="server-detail-page">
      <SEO
        title={`${server.name} — Discord Server`}
        description={`Join ${server.name} on Discord. ${server.description} · ${server.member_count?.toLocaleString?.() || 0} members · ${server.online_count?.toLocaleString?.() || 0} online. Listed on Serverlistr.`}
        path={`/servers/${server.slug || server.id}`}
        image={server.banner_url || server.icon_url}
        type="article"
        keywords={`${server.name}, ${(server.categories || []).join(", ")}, ${(server.tags || []).map((t) => "#" + t).join(", ")}, discord server, discord invite`}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: server.name,
            url: `${SITE_URL}/servers/${server.slug || server.id}`,
            logo: server.icon_url || undefined,
            image: server.banner_url || server.icon_url || undefined,
            description: server.description,
            sameAs: [server.invite_url],
            // Signal community size — Google uses this for surface treatments
            // like "X members · online now" in the SERP for entity results.
            interactionStatistic: [
              {
                "@type": "InteractionCounter",
                interactionType: { "@type": "https://schema.org/JoinAction" },
                userInteractionCount: server.member_count || 0,
              },
              {
                "@type": "InteractionCounter",
                interactionType: { "@type": "https://schema.org/BefriendAction" },
                userInteractionCount: server.online_count || 0,
              },
            ],
            keywords: [...(server.categories || []), ...(server.tags || [])].join(", "),
            aggregateRating: server.review_count
              ? {
                  "@type": "AggregateRating",
                  ratingValue: Number(server.avg_rating || 0).toFixed(1),
                  ratingCount: server.review_count,
                  bestRating: 5,
                  worstRating: 1,
                }
              : undefined,
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: `${server.name} — Discord Server`,
            url: `${SITE_URL}/servers/${server.slug || server.id}`,
            isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
            primaryImageOfPage: server.banner_url
              ? { "@type": "ImageObject", url: server.banner_url }
              : undefined,
          },
        ]}
      />
      {server.moderation_status && server.moderation_status !== "approved" && (
        <div
          className={`mb-4 rounded-2xl border p-4 text-sm ${
            server.moderation_status === "pending"
              ? "border-yellow-500/40 bg-yellow-500/[0.06] text-yellow-100"
              : "border-red-500/40 bg-red-500/[0.06] text-red-100"
          }`}
          data-testid="moderation-banner"
        >
          <div className="font-heading font-black text-base">
            {server.moderation_status === "pending"
              ? "⏳ Waiting on staff approval"
              : "❌ Listing rejected"}
          </div>
          <p className="mt-1 text-xs opacity-80">
            {server.moderation_status === "pending"
              ? "This listing isn't public yet. Only you and Serverlistr staff can see this page. Staff will review it shortly — most decisions happen within 24 hours."
              : "This listing was rejected by Serverlistr staff and isn't visible to the public."}
          </p>
          {server.moderation_note && (
            <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2 text-xs">
              <span className="font-semibold">Staff note: </span>
              {server.moderation_note}
            </div>
          )}
        </div>
      )}
      <Breadcrumbs
        items={[
          { name: "Discord Servers", path: "/servers" },
          ...(server.categories?.[0]
            ? [
                {
                  name: server.categories[0].charAt(0).toUpperCase() + server.categories[0].slice(1),
                  path: `/servers/category/${server.categories[0]}`,                },
              ]
            : []),
          { name: server.name },
        ]}
      />
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative rounded-3xl overflow-hidden bg-[#0A0A0F] border border-white/5 ${server.is_featured ? "neon-border" : ""}`}
      >
        <div
          className="relative h-56 sm:h-72"
          // Gradient always applied so a broken banner URL falls back
          // gracefully instead of showing an empty black bar with a
          // broken-image icon.
          style={{ background: detailBannerGradient(server.id) }}
        >
          {bannerUrl ? (
            <img
              src={bannerUrl}
              alt=""
              className="w-full h-full object-cover opacity-80"
              onError={() => setBannerBroken(true)}
            />
          ) : (
            <>
              <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15),transparent_55%)]" />
              <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_70%,rgba(255,255,255,0.1),transparent_50%)]" />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/40 to-transparent" />
          {server.is_featured && (
            <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 text-xs font-semibold text-neon-cyan border border-neon-cyan/40">
              <Sparkles className="w-3.5 h-3.5" /> Featured
            </div>
          )}
        </div>

        <div className="px-6 sm:px-10 pb-8">
          <div className="flex items-end gap-4 -mt-12 relative z-10">
            <img
              src={icon}
              alt=""
              className="w-24 h-24 rounded-3xl border-4 border-[#0A0A0F] object-cover bg-[#12121A]"
            />
            <div className="flex-1 pb-1 min-w-0">
              <h1 className="font-heading text-2xl sm:text-4xl font-black truncate" data-testid="server-detail-name">
                {server.name}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs sm:text-sm font-mono text-zinc-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-green" />
                  {server.online_count?.toLocaleString?.() ?? 0} online
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  {server.member_count?.toLocaleString?.() ?? 0} members
                </span>
                {server.review_count > 0 && (
                  <span className="inline-flex items-center gap-1.5" data-testid="detail-rating">
                    <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                    <span className="text-white font-semibold">
                      {Number(server.avg_rating || 0).toFixed(1)}
                    </span>
                    <span className="text-zinc-500">
                      ({server.review_count})
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {(server.categories || []).map((c) => (
              <Badge key={c} variant="outline" className="border-blurple/30 text-blurple bg-blurple/5">
                {c}
              </Badge>
            ))}
            {(server.tags || []).map((t) => (
              <Badge key={t} variant="outline" className="border-white/10 text-zinc-400 bg-white/5">
                #{t}
              </Badge>
            ))}
          </div>

          <p className="mt-6 text-base sm:text-lg text-zinc-300 leading-relaxed">
            {server.description}
          </p>
          {server.long_description ? (
            <p className="mt-4 text-sm text-zinc-400 whitespace-pre-wrap leading-relaxed">
              {server.long_description}
            </p>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={server.invite_url}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackJoinClick(server.id)}
              data-testid="detail-join-btn"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-bold transition-all hover:shadow-[0_0_30px_rgba(88,101,242,0.5)] active:scale-95"
            >
              Join Server <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={handleBump}
              disabled={onCooldown}
              data-testid="detail-bump-btn"
              title={onCooldown ? `Next bump ready in ${cooldownLabel}` : "Bump this server to the top"}
              className={
                onCooldown
                  ? "inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white/5 border border-white/10 font-bold text-white/50 cursor-not-allowed tabular-nums"
                  : "inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white/5 hover:bg-neon-pink/10 hover:text-neon-pink border border-white/10 hover:border-neon-pink/40 font-bold transition-all active:scale-95"
              }
            >
              <Rocket className={`w-4 h-4 ${onCooldown ? "opacity-60" : ""}`} />
              {onCooldown ? (
                <span data-testid="detail-bump-countdown">
                  Next bump ready: {cooldownLabel}
                </span>
              ) : (
                <span>Bump</span>
              )}
            </button>
            <ReportDialog server={server} />
          </div>

          {user?.user_id === server.owner_id && !server.is_featured && (
            <div className="mt-6 rounded-2xl border border-neon-cyan/40 bg-gradient-to-r from-neon-cyan/10 via-transparent to-blurple/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="owner-feature-cta">
              <div className="flex-1">
                <div className="font-heading text-base font-bold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-neon-cyan" /> Get this server featured
                </div>
                <p className="mt-1 text-xs text-zinc-400">
                  Pin to the top of every browse page, add a neon glow, and show up on the homepage. From $2.99.
                </p>
              </div>
              <button
                onClick={() => navigate(`/pricing?server=${server.id}`)}
                data-testid="owner-feature-btn"
                className="h-10 px-5 rounded-lg bg-neon-cyan text-black text-sm font-bold hover:bg-neon-cyan/90 transition-all active:scale-95"
              >
                See pricing
              </button>
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-4">
            <StatBlock
              label="Rating"
              value={
                server.review_count > 0
                  ? `${Number(server.avg_rating || 0).toFixed(1)} ★`
                  : "—"
              }
              isDate
            />
            <StatBlock label="Members" value={server.member_count} />
            <StatBlock label="Online" value={server.online_count} />
            <StatBlock
              label="Views"
              value={server.views_total || 0}
              testId="stat-views"
            />
            <StatBlock label="Listed" value={new Date(server.created_at).toLocaleDateString()} isDate />
          </div>
        </div>
      </motion.div>

      <ReviewsSection server={server} onServerUpdate={refreshServer} />

      <RelatedServers server={server} />

      <div className="mt-8 text-xs text-zinc-500">
        <Link to="/servers" className="hover:text-white">← Back to Discord server list</Link>
      </div>
    </div>
  );
}

function StatBlock({ label, value, isDate, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl bg-[#0A0A0F] border border-white/5 p-4"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="font-mono text-lg font-semibold">
        {isDate ? value : Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}
